import React from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Settings2, Loader2, AlertTriangle, Key } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import OrganizationSettingsForm from './components/OrganizationSettingsForm'
import ConfigKVManagement from './components/ConfigKVManagement'
import {
  OrganizationSettingResponse,
  OrganizationSettingUpdate
} from '@/app/types/organizationSettings'
import {
  getOrganizationSettings,
  updateOrganizationSettings
} from '@/app/services/organizationSettingsService'

const OrganizationSettingsPage: React.FC = () => {
  const queryClient = useQueryClient()

  const {
    data: settings,
    isLoading,
    isError,
    error
  } = useQuery<OrganizationSettingResponse>({
    queryKey: ['organizationSettings'],
    queryFn: getOrganizationSettings
  })

  const updateMutation = useMutation({
    mutationFn: updateOrganizationSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizationSettings'] })
      toast.success('Organization settings updated successfully')
    },
    onError: (error: Error) => {
      toast.error(`Failed to update settings: ${error.message}`)
    }
  })

  if (isLoading) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Organization Settings</h1>
        </div>
        <Card>
          <CardContent className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Organization Settings</h1>
        </div>
        <div className="flex items-center p-4 rounded-md bg-destructive/10 text-destructive border border-destructive">
          <AlertTriangle className="h-5 w-5 mr-3 flex-shrink-0" />
          <div>
            Error loading organization settings:{' '}
            {(error as Error)?.message || 'An unknown error occurred'}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Settings2 className="h-6 w-6" />
          <h1 className="text-2xl font-bold">Organization Settings</h1>
        </div>
      </div>

      <p className="text-muted-foreground">
        Manage default accounts, tax rates, operational settings, and configuration values for your organization.
      </p>

      <Tabs defaultValue="general" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="general">
            <Settings2 className="mr-2 h-4 w-4" /> General Settings
          </TabsTrigger>
          <TabsTrigger value="config-kv">
            <Key className="mr-2 h-4 w-4" /> Configuration Values
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <OrganizationSettingsForm
            onSubmit={updateMutation.mutate}
            defaultValues={settings || {}}
            isLoading={updateMutation.isPending}
          />
        </TabsContent>

        <TabsContent value="config-kv">
          <Card>
            <CardContent className="pt-6">
              <ConfigKVManagement />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default OrganizationSettingsPage
